// src/components/ChandlerLeadScore.jsx
// Compact lead quality card — appears at top of every vessel detail view.
import React, { useMemo } from "react";
import { calcLeadScore, LEAD_SCORE_THRESHOLDS, lookupVerifiedContact } from "../utils/chandlerData";
import "./ChandlerLeadScore.css";

function ScoreBar({ label, value, max, color }) {
  const pct = Math.round((value / max) * 100);
  return (
    <div className="cls-bar-row">
      <span className="cls-bar-label">{label}</span>
      <div className="cls-bar-track">
        <div className="cls-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="cls-bar-val">{value}/{max}</span>
    </div>
  );
}

export default function ChandlerLeadScore({ vessel, onOpenCRM }) {
  const data = useMemo(() => {
    if (!vessel) return null;

    const now = Date.now();
    const lastTs = vessel.last_position_at || vessel.effective_timestamp;
    const lastSeen = lastTs ? new Date(lastTs).getTime() : now;
    const daysAtSea = Math.max(0, (now - lastSeen) / (1000 * 60 * 60 * 24));

    const spd = Number(vessel.speed || vessel.speed_knots || 0);
    const lat = Number(vessel.latitude_degrees || vessel.lat || 0);
    const lng = Number(vessel.longitude_degrees || vessel.lng || 0);

    // Estimate distance to Singapore (1.264°N, 103.825°E)
    const SG_LAT = 1.264, SG_LNG = 103.825;
    const dlat = (SG_LAT - lat) * 111;
    const dlng = (SG_LNG - lng) * 111 * Math.cos((lat * Math.PI) / 180);
    const distNM = Math.sqrt(dlat * dlat + dlng * dlng) / 1.852;
    const etaHours = spd > 0.5 ? distNM / spd : 999;

    const dw = Number(vessel.deadweight || 0);
    const verifiedContact = lookupVerifiedContact(
      vessel.vessel_operator || vessel.registered_owner || vessel.vessel_name
    );
    const contactAvail = verifiedContact ? "agent" : vessel.registered_owner ? "operator" : null;

    const _vessel = { ...vessel, _daysAtSea: daysAtSea, _etaHours: etaHours };
    const total = calcLeadScore(_vessel, contactAvail);

    // Sub-scores
    const urgency  = etaHours < 6 ? 40 : etaHours < 12 ? 30 : etaHours < 24 ? 20 : etaHours < 48 ? 10 : 0;
    const size     = dw > 50000 ? 30 : dw > 20000 ? 20 : dw > 5000 ? 10 : dw > 0 ? 5 : 0;
    const freshness= daysAtSea > 14 ? 20 : daysAtSea >= 7 ? 12 : daysAtSea >= 3 ? 6 : 0;
    const contact  = contactAvail === "agent" ? 10 : contactAvail === "operator" ? 5 : 0;

    let label, color;
    if (total >= LEAD_SCORE_THRESHOLDS.HOT)     { label = "HOT LEAD";     color = "#00ff9d"; }
    else if (total >= LEAD_SCORE_THRESHOLDS.WARM){ label = "WARM LEAD";    color = "#ffaa00"; }
    else if (total >= LEAD_SCORE_THRESHOLDS.MONITOR){ label = "MONITOR";   color = "#00e5ff"; }
    else                                          { label = "LOW PRIORITY"; color = "#4a6080"; }

    const etaStr = etaHours < 999
      ? etaHours < 1 ? `${Math.round(etaHours * 60)}m` : `${Math.floor(etaHours)}h ${Math.round((etaHours % 1) * 60)}m`
      : "—";

    return { total, label, color, urgency, size, freshness, contact, etaStr, daysAtSea: daysAtSea.toFixed(1), verifiedContact };
  }, [vessel]);

  if (!vessel || !data) return null;

  return (
    <div className="cls-card">
      <div className="cls-left">
        <div className="cls-score" style={{ color: data.color }}>{data.total}</div>
        <div className="cls-label" style={{ color: data.color }}>{data.label}</div>
        <div className="cls-meta">
          <span className="cls-meta-item">⏱ ETA ~{data.etaStr}</span>
          <span className="cls-meta-item">🌊 {data.daysAtSea}d at sea</span>
        </div>
      </div>
      <div className="cls-bars">
        <ScoreBar label="URGENCY"  value={data.urgency}   max={40} color="#ff4466" />
        <ScoreBar label="SIZE"     value={data.size}       max={30} color="#ffaa00" />
        <ScoreBar label="FRESHNESS"value={data.freshness}  max={20} color="#00e5ff" />
        <ScoreBar label="CONTACT"  value={data.contact}    max={10} color="#00ff9d" />
      </div>
      <div className="cls-right">
        {data.verifiedContact && (
          <div className="cls-verified">
            <span className="cls-v-badge">✓ CONTACT VERIFIED</span>
            <span className="cls-v-email">{data.verifiedContact.email}</span>
          </div>
        )}
        <button className="cls-cta" onClick={() => onOpenCRM?.()}>
          CONTACT NOW
        </button>
      </div>
    </div>
  );
}